import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const RealTimeCostBreakdown = ({ costs, isCalculating, projectData }) => {
  const costItems = [
    { 
      label: 'Material (Filamento)', 
      value: costs?.totalMaterial || 0, 
      icon: 'Package', 
      color: 'text-primary',
      description: 'Custo dos filamentos utilizados'
    },
    { 
      label: 'Energia Elétrica', 
      value: costs?.totalElectricity || 0, 
      icon: 'Zap', 
      color: 'text-warning',
      description: 'Consumo energético da impressora'
    },
    { 
      label: 'Mão de Obra', 
      value: costs?.totalLabor || 0, 
      icon: 'User', 
      color: 'text-secondary',
      description: 'Tempo de setup, monitoramento e pós-processamento'
    },
    { 
      label: 'Depreciação Equipamento', 
      value: costs?.totalDepreciation || 0, 
      icon: 'Settings', 
      color: 'text-accent',
      description: 'Desgaste e manutenção da impressora'
    },
    {
      label: 'Embalagens',
      value: costs?.packagingCost || 0,
      icon: 'PackageCheck',
      color: 'text-primary',
      description: 'Preço unitário x quantidade de peças'
    },
    { 
      label: 'Desperdício', 
      value: costs?.waste || 0, 
      icon: 'Trash2', 
      color: 'text-destructive',
      description: `Total de desperdício: ${(costs?.totalWasteGrams || 0)?.toFixed(1)}g`
    }
  ];

  const totalCost = costs?.total || 0;
  const profit = costs?.profit || 0;
  const finalPrice = costs?.finalPrice || 0;
  const marketplaceEnabled = !!projectData?.marketplaceEnabled;
  const marketplaceFeesTotal = costs?.marketplaceFeesTotal || 0;
  const marketplaceFeesPercent = costs?.marketplaceFeesPercent || 0;
  const marketplaceFeesFixed = costs?.marketplaceFeesFixed || 0;
  const marketplaceBreakdown = costs?.marketplaceBreakdown || [];
  const showMarketplace = marketplaceEnabled || marketplaceBreakdown?.length > 0;

  if (isCalculating) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 animate-fade-in">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">Calculando Orçamento...</h3>
            <p className="text-muted-foreground">Processando custos com base no mercado brasileiro</p>
          </div>
        </div>
      </div>
    );
  }

  if (totalCost === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 animate-fade-in text-center py-16">
        <Icon name="Calculator" size={64} className="mx-auto mb-6 text-muted-foreground opacity-50" />
        <h3 className="text-2xl font-semibold text-foreground mb-4">Aguardando Cálculo</h3>
        <p className="text-muted-foreground text-lg">Complete os parâmetros para ver o breakdown de custos</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Resumo rápido: Peso total da impressão e Desperdício */}
      {Array.isArray(costs?.filamentBreakdown) && costs?.filamentBreakdown?.length > 0 && (
        (() => {
          const totalWeightUnits = costs?.filamentBreakdown?.reduce((sum, f) => sum + (parseFloat(f?.weight || 0) || 0), 0) || 0;
          const totalWasteUnits = costs?.filamentBreakdown?.reduce((sum, f) => sum + (parseFloat(f?.waste || 0) || 0), 0) || 0;
          const totalWeightGrams = totalWeightUnits; // por impressão
          const totalWasteGrams = totalWasteUnits; // por impressão
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-card border border-border/50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-semibold text-foreground">Peso total da impressão</h5>
                    <p className="text-xs text-muted-foreground">Somatório dos filamentos</p>
                  </div>
                  <div className="text-xl font-bold text-foreground">{totalWeightGrams?.toFixed(1)}g</div>
                </div>
              </div>
              <div className="bg-card border border-border/50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-semibold text-foreground">Desperdício</h5>
                    <p className="text-xs text-muted-foreground">Total das perdas</p>
                  </div>
                  <div className="text-xl font-bold text-destructive">{totalWasteGrams?.toFixed(1)}g</div>
                </div>
              </div>
              <div className="bg-card border border-border/50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-semibold text-foreground">Custo dos Filamentos</h5>
                    <p className="text-xs text-muted-foreground">Material + desperdício</p>
                  </div>
                  <div className="text-xl font-bold text-primary">R$ {costs?.totalMaterial?.toFixed(2)}</div>
                </div>
              </div>
            </div>
          );
        })()
      )}
      {/* Parâmetros Técnicos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-semibold text-foreground">Tarifa de Energia</h5>
              <p className="text-xs text-muted-foreground">R$/kWh aplicada</p>
            </div>
            <div className="text-xl font-bold text-foreground">R$ {(parseFloat(projectData?.electricityRate || 0))?.toFixed(2)}</div>
          </div>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-semibold text-foreground">Desperdício de Material</h5>
              <p className="text-xs text-muted-foreground">Percentual configurado</p>
            </div>
            <div className="text-xl font-bold text-foreground">{(parseFloat(projectData?.materialWastePct || 0))?.toFixed(1)}%</div>
          </div>
        </div>
      </div>
      {/* Cost Breakdown */}
      <div className="bg-card border border-border rounded-xl p-8 animate-fade-in">

        <div className="flex items-center space-x-4 mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-xl flex items-center justify-center shadow-lg">
            <Icon name="PieChart" size={28} className="text-primary" />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-foreground">Breakdown de Custos</h3>
            <p className="text-muted-foreground text-lg">Análise detalhada dos componentes de custo</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {costItems?.map((item, index) => (
            <motion.div
              key={item?.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-card border border-border/50 rounded-xl p-4 hover:shadow-elevation-2 hover:border-primary/20 cost-item"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-muted/20 to-surface/20 rounded-xl flex items-center justify-center">
                  <Icon name={item?.icon} size={20} className={item?.color} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground text-sm">{item?.label}</h4>
                  <p className="text-xs text-muted-foreground mb-1">{item?.description}</p>
                  <div className="text-xl font-bold text-foreground">
                    R$ {item?.value?.toFixed(2)}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Resumo dos Filamentos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-semibold text-foreground">Custo dos Filamentos</h5>
                <p className="text-xs text-muted-foreground">Material + desperdício (total)</p>
              </div>
              <div className="text-xl font-bold text-primary">R$ {costs?.totalMaterial?.toFixed(2)}</div>
            </div>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-semibold text-foreground">Desperdício Total</h5>
                <p className="text-xs text-muted-foreground">{(costs?.totalWasteGrams || 0)?.toFixed(1)}g</p>
              </div>
              <div className="text-xl font-bold text-destructive">R$ {costs?.waste?.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Filament Breakdown */}
        {costs?.filamentBreakdown?.length > 0 && (
          <div className="mb-8">
            <h4 className="text-lg font-bold text-foreground mb-4 flex items-center">
              <Icon name="Layers" size={20} className="mr-2 text-primary" />
              Detalhamento por Filamento
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {costs?.filamentBreakdown?.map((filament, index) => (
                <motion.div
                  key={`${filament?.id}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="bg-card border border-border/50 rounded-xl p-4"
                >
                  <div className="flex items-center space-x-4">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: filament?.color_hex || '#666666' }}
                    >
        <Icon name="Filament" size={16} className="text-white drop-shadow-lg" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-foreground">
                        {filament?.brand} {filament?.material}
                      </h5>
                      <p className="text-xs text-muted-foreground">
                        {filament?.color} • {filament?.weight}g + {filament?.waste || 0}g desperdício
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-lg font-bold text-primary">
                          R$ {filament?.cost?.toFixed(2)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {filament?.percentage?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="text-xs text-muted-foreground">
                          Custo desperdício: <span className="font-semibold text-destructive">R$ {parseFloat(filament?.wasteCost || 0)?.toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground text-right">
                          Custo por peça: <span className="font-semibold">R$ { (parseFloat(projectData?.quantity || 1) > 0 ? (parseFloat(filament?.cost || 0) / parseFloat(projectData?.quantity || 1)) : 0)?.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        SKU: {filament?.sku || '—'} • Estoque: {filament?.stock_grams != null ? `${parseFloat(filament?.stock_grams || 0)?.toFixed(1)}g` : '—'}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Cost */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border/50 rounded-xl p-6 bg-gradient-to-br from-muted/10 to-surface/10"
        >
          <div className="text-center">
            <Icon name="Calculator" size={32} className="mx-auto mb-4 text-accent" />
            <h4 className="text-lg font-semibold text-foreground mb-2">Custo Total</h4>
            <div className="text-3xl font-bold text-foreground">
              R$ {totalCost?.toFixed(2)}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Custo de produção base
            </p>
          </div>
        </motion.div>

        {/* Profit */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-success/20 rounded-xl p-6 bg-gradient-to-br from-success/10 to-primary/10"
        >
          <div className="text-center">
            <Icon name="TrendingUp" size={32} className="mx-auto mb-4 text-success" />
            <h4 className="text-lg font-semibold text-foreground mb-2">Lucro Estimado</h4>
            <div className="text-3xl font-bold text-success">
              R$ {profit?.toFixed(2)}
            </div>
            {/* Removido texto de margem aplicada para evitar redundância com parâmetros */}
          </div>
        </motion.div>

      {/* Final Price */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-card border border-primary/20 rounded-xl p-6 bg-gradient-to-br from-primary/10 to-secondary/10"
      >
        <div className="text-center">
          <Icon name="DollarSign" size={32} className="mx-auto mb-4 text-primary" />
          <h4 className="text-lg font-semibold text-foreground mb-2">Preço Final</h4>
          <div className="text-4xl font-bold text-primary">
            R$ {finalPrice?.toFixed(2)}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            R$ {costs?.perUnit?.toFixed(2)} por unidade
          </p>
        </div>
      </motion.div>

      {/* Unit Price Highlight when quantity > 1 */}
      {(() => {
        const quantity = parseInt(projectData?.quantity) || 1;
        if (quantity > 1) {
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.52 }}
              className="bg-card border border-secondary/20 rounded-xl p-6 bg-gradient-to-br from-secondary/10 to-accent/10"
            >
              <div className="text-center">
        <Icon name="Filament" size={28} className="mx-auto mb-3 text-secondary" />
                <h4 className="text-lg font-semibold text-foreground mb-1">Preço por Peça</h4>
                <div className="text-3xl font-bold text-foreground">
                  R$ {costs?.perUnit?.toFixed(2)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{quantity} peças no mesmo plate</p>
                {parseFloat(costs?.perUnitWithoutMarketplace || 0) > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Preço/peça s/ marketplace: <span className="font-semibold text-foreground">R$ {parseFloat(costs?.perUnitWithoutMarketplace)?.toFixed(2)}</span>
                  </p>
                )}
              </div>
            </motion.div>
          );
        }
        return null;
      })()}
      </div>

      {/* Marketplace Fees Summary */}
      {showMarketplace && (marketplaceFeesTotal > 0 || marketplaceFeesPercent > 0 || marketplaceFeesFixed > 0 || marketplaceBreakdown?.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="bg-card border border-primary/20 rounded-xl p-6 bg-gradient-to-br from-primary/5 to-secondary/5"
        >
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-xl flex items-center justify-center">
              <Icon name="ShoppingCart" size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="text-xl font-bold text-foreground">Taxas de Marketplace</h4>
              <p className="text-muted-foreground">Aplicadas ao preço final</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">R$ {marketplaceFeesTotal?.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">Total de taxas</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-semibold text-foreground mb-1">R$ {marketplaceFeesPercent?.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">Percentual sobre preço</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-semibold text-foreground mb-1">R$ {marketplaceFeesFixed?.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">Fixa por transação</div>
            </div>
          </div>

          {marketplaceBreakdown?.length > 0 && (
            <div className="mt-6">
              <h5 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                <Icon name="Store" size={18} className="mr-2 text-primary" />
                Marketplaces Selecionados
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {marketplaceBreakdown?.map((mkt, idx) => (
                  <div key={`${mkt?.marketplaceId}-${idx}`} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-semibold text-foreground">{mkt?.name || 'Marketplace'}</div>
                        <div className="text-xs text-muted-foreground">Qtd: {mkt?.quantity} • Taxa {parseFloat(mkt?.percent || 0)?.toFixed(1)}% • Fixa R$ {parseFloat(mkt?.fixed_per_unit || 0)?.toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center">
                        <div className="text-xl font-bold text-foreground">R$ {parseFloat(mkt?.perUnitPrice || 0)?.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">Preço por peça</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-primary">R$ {parseFloat(mkt?.totalPrice || 0)?.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">Preço total</div>
                      </div>
                    </div>
                    {mkt?.targetUnitPrice > 0 && (
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div className="text-center">
                          <div className="text-sm font-semibold text-foreground">R$ {parseFloat(mkt?.targetUnitPrice)?.toFixed(2)}</div>
                          <div className="text-xs text-muted-foreground">Preço alvo/peça</div>
                        </div>
                        <div className={`text-center`}>
                          <div className={`text-sm font-semibold ${ (mkt?.unitMargin || 0) >= 0 ? 'text-success' : 'text-destructive' }`}>R$ {parseFloat(mkt?.unitMargin || 0)?.toFixed(2)}</div>
                          <div className="text-xs text-muted-foreground">Margem por peça</div>
                        </div>
                        <div className={`text-center`}>
                          <div className={`text-sm font-semibold ${ (mkt?.unitMarginPercent || 0) >= 0 ? 'text-success' : 'text-destructive' }`}>{mkt?.unitMarginPercent != null ? `${parseFloat(mkt?.unitMarginPercent)?.toFixed(1)}%` : '-'}</div>
                          <div className="text-xs text-muted-foreground">Margem %</div>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="text-center">
                        <div className="text-sm font-semibold text-foreground">R$ {parseFloat(mkt?.percentFeeAmount || 0)?.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">Taxa percentual</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-semibold text-foreground">R$ {parseFloat(mkt?.fixedFeeAmount || 0)?.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">Taxa fixa</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
      {/* Análise de Mercado removida para evitar duplicação de quantidade/duração e aglomeração de informações */}
    </div>
  );
};

export default RealTimeCostBreakdown;
